import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';

const backgroundImages = [
  { id: '1', url: '/backgrounds/mountain-lake.jpg.png', name: 'Lago Montañoso' },
  { id: '2', url: '/backgrounds/forest-stream.jpg.png', name: 'Arroyo del Bosque' },
  { id: '3', url: '/backgrounds/valley-river.jpg.png', name: 'Valle con Río' },
];

// Gradientes de fallback basados en las descripciones de las imágenes
export const fallbackGradients: { [key: string]: string } = {
  '1': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Lago Montañoso - tonos azul/púrpura
  '2': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', // Arroyo del Bosque - tonos verde
  '3': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Valle con Río - tonos azul claro
};

export const useBackgroundImage = () => {
  const { user } = useAuth();
  const [selectedBackground, setSelectedBackground] = useState<string>('1');
  const [imageLoaded, setImageLoaded] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    // Cargar fondo desde localStorage primero (para usuarios no autenticados)
    const savedBg = localStorage.getItem('background_image');
    if (savedBg && backgroundImages.find(bg => bg.id === savedBg)) {
      setSelectedBackground(savedBg);
    }
    
    // Si hay usuario autenticado, usar su preferencia
    if (user?.background_image) {
      setSelectedBackground(user.background_image);
      localStorage.setItem('background_image', user.background_image);
    }
  }, [user]);

  useEffect(() => {
    // Pre-cargar imágenes para verificar si están disponibles
    const checkImage = (bgId: string, url: string) => {
      const img = new Image();
      img.onload = () => {
        setImageLoaded(prev => ({ ...prev, [bgId]: true }));
      };
      img.onerror = () => {
        setImageLoaded(prev => ({ ...prev, [bgId]: false }));
      };
      img.src = url;
    };

    backgroundImages.forEach((bg) => {
      checkImage(bg.id, bg.url);
    });
  }, []);

  const changeBackground = async (bgId: string) => {
    setSelectedBackground(bgId);
    localStorage.setItem('background_image', bgId);
    
    // Si hay usuario autenticado, guardar en su perfil
    if (user) {
      try {
        await apiService.updateProfile({ background_image: bgId });
      } catch (error) {
        logger.error('Error guardando preferencia de fondo:', error);
      }
    }
  };

  const currentBg = backgroundImages.find(bg => bg.id === selectedBackground) || backgroundImages[0];
  const fallbackGradient = fallbackGradients[selectedBackground] || fallbackGradients['1'];
  // Usar fallback solo si la imagen explícitamente falló al cargar
  // Si es undefined, intentamos mostrar la imagen (puede estar cargando)
  const useFallback = imageLoaded[selectedBackground] === false;

  return {
    selectedBackground,
    currentBg,
    backgroundImages,
    changeBackground,
    fallbackGradient,
    fallbackGradients,
    useFallback,
  };
};
